// Job parts — the ONE place a job moves inventory (operator ruling 2026-10-07: industry standard).
//
// Parts come off the shelf ONCE, when the job is completed, in the quantity actually used, from
// the location they were actually taken from. At completion the tech / office confirms each
// planned part (the work order's line items that carry an inventory_item_id), can change the
// quantity used, and can add parts that were not on the job; added parts become billable lines.
//
// It replaced a chain that never worked: approve wrote a POSITIVE job_reserved row (which the
// stock trigger counts as stock RECEIVED), start deducted, completion copied the reservation's
// +qty into a job_used row (adding stock back), and the cancel/reject "releases" wrote enum
// values that do not exist. Measured 2026-10-07: exactly one ledger row had ever been tied to a
// work order, so there was no history to migrate.
//
// Stock mechanics (do not duplicate): every inventory_ledger insert is applied to inventory_stock
// by trigger apply_ledger_to_inventory_stock at COALESCE(to_location_id, from_location_id), and the
// UI reads the inventory_stock_calculated view. So "used" is a job_used row with from_location_id
// and a NEGATIVE quantity_delta; giving parts back is a job_returned row with to_location_id and a
// POSITIVE delta. The ledger is append-only — corrections are new rows, never edits.

import { supabase } from '../utils/supabaseClient';
import { HEADER_BILLED_MODELS, pricesFromLineItems } from '@tradesmatepro/shared-ui';

const keyOf = (itemId, locationId) => `${itemId}|${locationId}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Per item + location for this job: `posted` = net taken off the shelf (positive = taken out);
// `reserved` = net held for the job by "Allocate to job" (job_reserved rows: + reserve, - release).
// A reservation does not move stock (20261007_inventory_reservations_hold_not_move), so it is
// never counted as taken.
const loadLedger = async (workOrderId, companyId) => {
  const { data, error } = await supabase
    .from('inventory_ledger')
    .select('item_id, from_location_id, to_location_id, quantity_delta, reason')
    .eq('company_id', companyId)
    .eq('work_order_id', workOrderId)
    .in('reason', ['job_used', 'job_returned', 'job_reserved']);
  if (error) throw error;
  const posted = new Map();
  const reserved = new Map();
  for (const row of data || []) {
    const loc = row.to_location_id || row.from_location_id;
    if (!loc) continue;
    const k = keyOf(row.item_id, loc);
    if (row.reason === 'job_reserved') reserved.set(k, (reserved.get(k) || 0) + num(row.quantity_delta));
    else posted.set(k, (posted.get(k) || 0) - num(row.quantity_delta));
  }
  return { posted, reserved };
};
const loadPosted = async (workOrderId, companyId) => (await loadLedger(workOrderId, companyId)).posted;

// Release everything still reserved for this job (completion, cancel, reject). Returns rows written.
const releaseReservations = async (workOrderId, companyId, userId, note) => {
  const { reserved } = await loadLedger(workOrderId, companyId);
  const rows = [...reserved.entries()]
    .filter(([, q]) => q > 1e-9)
    .map(([k, q]) => {
      const [itemId, locationId] = k.split('|');
      return { company_id: companyId, item_id: itemId, to_location_id: locationId, quantity_delta: -q,
               reason: 'job_reserved', work_order_id: workOrderId, created_by: userId || null, notes: note };
    });
  if (rows.length) {
    const { error } = await supabase.from('inventory_ledger').insert(rows);
    if (error) throw error;
  }
  return rows.length;
};

const JobPartsService = {
  /**
   * Everything the "Parts used" step needs for one job.
   * @returns {{pricingModel, planned, posted, locations, items, stock, defaultLocationId}}
   */
  async load(workOrderId, companyId, userId) {
    const [woRes, linesRes, locRes, itemRes, stockRes, ledger] = await Promise.all([
      supabase.from('work_orders').select('pricing_model, prices_from_line_items').eq('id', workOrderId).eq('company_id', companyId).maybeSingle(),
      supabase
        .from('work_order_line_items')
        .select('id, description, quantity, unit_price, inventory_item_id, sort_order')
        .eq('work_order_id', workOrderId)
        .eq('company_id', companyId)
        .not('inventory_item_id', 'is', null)
        .order('sort_order', { ascending: true }),
      supabase
        .from('inventory_locations')
        .select('id, name, type, assigned_technician_id')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name'),
      supabase
        .from('inventory_items')
        .select('id, name, sku, sell_price, cost_price, unit_of_measure')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name'),
      supabase
        .from('inventory_stock_calculated')
        .select('item_id, location_id, quantity_on_hand')
        .eq('company_id', companyId),
      loadLedger(workOrderId, companyId)
    ]);
    const { posted, reserved } = ledger;
    for (const r of [woRes, linesRes, locRes, itemRes, stockRes]) if (r.error) throw r.error;

    const items = itemRes.data || [];
    const itemById = new Map(items.map((i) => [i.id, i]));
    const stock = new Map((stockRes.data || []).map((s) => [keyOf(s.item_id, s.location_id), num(s.quantity_on_hand)]));
    const locations = locRes.data || [];
    // The tech's own truck when one is assigned to them; otherwise the caller picks per part.
    const myTruck = locations.find((l) => l.type === 'vehicle' && userId && l.assigned_technician_id === userId);

    // Re-completion: what each item already has out, per location, handed to the planned lines in
    // order; whatever is left over (e.g. an unbilled part added last time) comes back as an added
    // row so it is not silently returned to stock.
    const remaining = new Map([...posted.entries()].filter(([, q]) => q > 0));
    const takeRemaining = (itemId, preferredLoc, upTo) => {
      const keys = [...remaining.keys()].filter((k) => k.startsWith(`${itemId}|`))
        .sort((a, b) => (a.endsWith(`|${preferredLoc}`) ? -1 : b.endsWith(`|${preferredLoc}`) ? 1 : 0));
      for (const k of keys) {
        const q = remaining.get(k);
        const take = Math.min(q, upTo);
        if (take <= 0) continue;
        if (q - take > 1e-9) remaining.set(k, q - take); else remaining.delete(k);
        return { qty: take, locationId: k.split('|')[1] };
      }
      return null;
    };
    const hasPosted = remaining.size > 0;

    const planned = (linesRes.data || []).map((line) => {
      const item = itemById.get(line.inventory_item_id);
      // Where it was already taken from (a reopened job), else my truck, else wherever it is stocked.
      const postedLoc = [...posted.entries()].find(([k, q]) => k.startsWith(`${line.inventory_item_id}|`) && q > 0)?.[0]?.split('|')[1];
      // Where it was allocated (reserved) for this job, if it was.
      const reservedLoc = [...reserved.entries()].find(([k, q]) => k.startsWith(`${line.inventory_item_id}|`) && q > 0)?.[0]?.split('|')[1];
      const stockedLoc = locations
        .map((l) => ({ id: l.id, qty: stock.get(keyOf(line.inventory_item_id, l.id)) || 0 }))
        .sort((a, b) => b.qty - a.qty)[0];
      return {
        lineItemId: line.id,
        itemId: line.inventory_item_id,
        name: item?.name || line.description,
        sku: item?.sku || '',
        plannedQty: num(line.quantity),
        unitPrice: num(line.unit_price),
        defaultLocationId: postedLoc || reservedLoc || myTruck?.id || (stockedLoc && stockedLoc.qty > 0 ? stockedLoc.id : locations[0]?.id) || ''
      };
    }).map((row) => {
      // A part this job never moved defaults to the quote; one it already moved defaults to what
      // it moved (so reopening and re-completing an untouched job changes nothing).
      const everMoved = [...posted.keys()].some((k) => k.startsWith(`${row.itemId}|`));
      if (!hasPosted || !everMoved) return { ...row, used: row.plannedQty, locationId: row.defaultLocationId };
      const got = takeRemaining(row.itemId, row.defaultLocationId, row.plannedQty);
      return got ? { ...row, used: got.qty, locationId: got.locationId } : { ...row, used: 0, locationId: row.defaultLocationId };
    });
    const extras = [...remaining.entries()].map(([k, q]) => {
      const [itemId, locationId] = k.split('|');
      return { itemId, locationId, qty: q, bill: false, alreadyPosted: true };
    });
    // Parts allocated to this job from Inventory that are not on the job's lines: offer them as
    // added parts (prefilled with the allocated quantity) so the user confirms what was used.
    const plannedItems = new Set(planned.map((p) => p.itemId));
    const postedItems = new Set(extras.map((e) => e.itemId));
    for (const [k, q] of reserved.entries()) {
      const [itemId, locationId] = k.split('|');
      if (q > 1e-9 && !plannedItems.has(itemId) && !postedItems.has(itemId)) {
        extras.push({ itemId, locationId, qty: q, bill: true, fromReservation: true });
      }
    }

    // C: on header-billed jobs (flat rate not built from lines, percentage, unit) the total comes
    // from the header, so an added line would not be charged. Say so instead of offering "Bill".
    const wo = woRes.data || {};
    const model = String(wo.pricing_model || '').toUpperCase();
    const addedPartsBillable = !(HEADER_BILLED_MODELS.includes(model) && !pricesFromLineItems(wo));

    return {
      pricingModel: woRes.data?.pricing_model || null,
      addedPartsBillable,
      planned,
      extras,
      posted,
      reserved,
      locations,
      items,
      stock,
      defaultLocationId: myTruck?.id || locations[0]?.id || ''
    };
  },

  /**
   * Record what was used. Posts only the DIFFERENCE from what this job already took out, so it is
   * safe to run again when a job is reopened and re-completed.
   *
   * @param {object} p
   * @param {Array<{lineItemId?:string, itemId:string, locationId:string, used:number}>} p.planned
   * @param {Array<{itemId:string, locationId:string, qty:number, bill:boolean}>} p.added
   * @param {string|null} p.pricingModel  TIME_MATERIALS bills what was used (planned lines follow the used qty)
   */
  async record({ workOrderId, companyId, userId, planned = [], added = [], pricingModel, items = [], addedPartsBillable = true }) {
    const itemById = new Map(items.map((i) => [i.id, i]));
    const want = new Map();
    const add = (itemId, locationId, qty) => {
      if (!itemId || !locationId || !(qty > 0)) return;
      const k = keyOf(itemId, locationId);
      want.set(k, (want.get(k) || 0) + qty);
    };
    for (const p of planned) add(p.itemId, p.locationId, num(p.used));
    for (const a of added) add(a.itemId, a.locationId, num(a.qty));
    for (const a of added) {
      if (!a.itemId || !a.locationId || !(num(a.qty) > 0)) throw new Error('Each added part needs a part, a quantity and a location.');
    }

    // Ledger: the difference between what is used now and what this job already took out.
    const posted = await loadPosted(workOrderId, companyId);
    const ledgerRows = [];
    for (const k of new Set([...want.keys(), ...posted.keys()])) {
      const [itemId, locationId] = k.split('|');
      const diff = (want.get(k) || 0) - (posted.get(k) || 0);
      if (Math.abs(diff) < 1e-9) continue;
      const unitCost = itemById.get(itemId)?.cost_price ?? null;
      ledgerRows.push(diff > 0
        ? { company_id: companyId, item_id: itemId, from_location_id: locationId, quantity_delta: -diff,
            reason: 'job_used', work_order_id: workOrderId, created_by: userId || null, unit_cost: unitCost,
            notes: 'Used on job (confirmed at completion)' }
        : { company_id: companyId, item_id: itemId, to_location_id: locationId, quantity_delta: -diff,
            reason: 'job_returned', work_order_id: workOrderId, created_by: userId || null, unit_cost: unitCost,
            notes: 'Returned to stock (not used on job)' });
    }
    if (ledgerRows.length) {
      const { error } = await supabase.from('inventory_ledger').insert(ledgerRows);
      if (error) throw error;
    }

    // Billing. Added parts the customer pays for become material lines. On time & materials the
    // customer pays for what was used, so planned part lines follow the used quantity (a part
    // that was not used at all comes off the bill). Other pricing models bill what was quoted.
    const billable = addedPartsBillable === false ? [] : added.filter((a) => a.bill);
    // Ruling "One part, one price" (2026-10-24, extended to completion 2026-10-25): the line is
    // written and PRICED by the database (add_completion_part_lines) — the linked active Price
    // Book sell price, else the inventory sell price; cost stays the inventory cost — so office
    // and crew bill the same price and no Price Book price is sent to the browser.
    if (billable.length) {
      const { error: rpcError } = await supabase.rpc('add_completion_part_lines', {
        p_work_order_id: workOrderId,
        p_parts: billable.map((a) => ({ inventory_item_id: a.itemId, quantity: num(a.qty) }))
      });
      if (rpcError) throw rpcError;
    }

    if (pricingModel === 'TIME_MATERIALS') {
      for (const p of planned) {
        if (!p.lineItemId) continue;
        const used = num(p.used);
        if (Math.abs(used - num(p.plannedQty)) < 1e-9) continue;
        const res = used > 0
          ? await supabase.from('work_order_line_items')
              .update({ quantity: used, total_price: used * num(p.unitPrice) })
              .eq('id', p.lineItemId).eq('company_id', companyId)
          : await supabase.from('work_order_line_items')
              .delete()
              .eq('id', p.lineItemId).eq('company_id', companyId);
        if (res.error) throw res.error;
      }
    }

    const released = await releaseReservations(workOrderId, companyId, userId, 'Allocation released at job completion');

    return { ledgerRows: ledgerRows.length, linesAdded: billable.length, released };
  }
};

JobPartsService.releaseReservations = releaseReservations;

export default JobPartsService;
